import React from 'react';
import { Button } from '@/components/ui/button';
import { PanelRecordingPreview } from '@/components/PanelRecordingPreview';
import { VideoPreviewCard } from '@/components/VideoPreviewCard';
import { StarRating } from '@/components/StarRating';
import { HrScoreRings } from '@/components/HrScoreRings';
import {
  Play,
  Sparkles,
  Video as VideoIcon,
  ClipboardList,
  Users,
  Eye,
  User,
  BadgeCheck,
} from 'lucide-react';
import { classNames } from '@/lib/format';
import { resolveScore } from '@/lib/reportConstants';
import {
  deriveMcqCategoriesFromItems,
  panelFactorScores,
  PROFILE_BADGE_ICONS,
  PROFILE_BADGE_TONE,
} from '@/lib/clientReportHelpers';
import { Module } from './MagazineReportParts';
import { CLIENT_ARTEFACT_UNAVAILABLE_MSG } from '@/lib/evalErrors';

export function SoftUnavailableSection({ id, eyebrow, title, icon, message }) {
  return (
    <Module id={id} eyebrow={eyebrow} title={title} icon={icon} inlineTitle>
      <p
        className="rounded-xl border border-border/60 bg-secondary/30 px-4 py-3 text-[13.5px] text-muted-foreground"
        data-testid={`artefact-unavailable-${id}`}
      >
        {message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
      </p>
    </Module>
  );
}

export function ProfileSummarySection({
  c,
  profileTitle,
  keyHighlights,
  companyResume,
  certBadges,
  onOpenResume,
}) {
  const renderCertBadges = ({ className, testId = 'profile-summary-cert-badges', fullWidth = false }) => (
    certBadges.length > 0 ? (
      <div className={className} data-testid={testId}>
        {certBadges.map((cert, i) => {
          const Icon = PROFILE_BADGE_ICONS[cert.kind] || BadgeCheck;
          const tone = PROFILE_BADGE_TONE[cert.kind] || PROFILE_BADGE_TONE.certification;
          return (
            <span
              key={`${cert.kind}-${cert.name}-${testId}-${i}`}
              className={classNames(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold',
                fullWidth ? 'max-w-full' : 'max-w-[220px]',
                tone,
              )}
              data-kind={cert.kind}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{cert.name}</span>
            </span>
          );
        })}
      </div>
    ) : null
  );

  return (
    <Module id="opening" eyebrow="Profile" title="Profile Summary" icon={User} inlineTitle>
      {/* Mobile: name left + avatar right (inline, centered). md+: two-column layout unchanged. */}
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-x-5 md:gap-y-0"
        data-testid="profile-summary-hero"
      >
        <div className="flex items-center gap-3 md:contents">
          <div className="min-w-0 flex-1 md:col-start-1">
            <h3 className="text-[21px] font-extrabold tracking-tight leading-tight" data-testid="profile-summary-name">{c.full_name}</h3>
            {profileTitle && (
              <p className="mt-0.5 text-[12.5px] text-muted-foreground" data-testid="profile-summary-title">{profileTitle}</p>
            )}
          </div>
          <div
            className={classNames(
              'flex shrink-0 flex-col items-end gap-2.5',
              'md:col-start-2 md:row-start-1',
              (keyHighlights.length > 0 || companyResume) && 'md:row-span-2',
            )}
            data-testid="profile-summary-aside"
          >
            <div
              className="grid h-[64px] w-[64px] place-items-center overflow-hidden rounded-2xl border bg-secondary text-[20px] font-bold text-foreground/70 md:h-[76px] md:w-[76px] md:text-[24px]"
              data-testid="profile-summary-photo-widget"
            >
              {c.photo_url ? (
                <img src={c.photo_url} alt="" className="h-full w-full object-cover" />
              ) : (
                (c.full_name || '?').split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?'
              )}
            </div>
            {renderCertBadges({
              className: 'hidden w-auto flex-col items-end gap-1.5 md:flex',
            })}
          </div>
        </div>
        {renderCertBadges({
          className: 'flex w-full flex-col items-start gap-1.5 md:hidden',
          testId: 'profile-summary-cert-badges-mobile',
          fullWidth: true,
        })}
        {(keyHighlights.length > 0 || companyResume) && (
          <div className="min-w-0 md:col-start-1 md:mt-3">
            {keyHighlights.length > 0 && (
              <div data-testid="profile-summary-key-highlights">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Key Highlights</p>
                <ul className="mt-1.5 space-y-1">
                  {keyHighlights.map((h, i) => (
                    <li key={i} className="flex gap-2 text-[12.5px] leading-[1.45] text-foreground/80">
                      <span className="mt-0.5 shrink-0 font-bold text-emerald-600" aria-hidden>✓</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {companyResume && (
              <div className={classNames('flex flex-wrap items-center gap-2', keyHighlights.length > 0 ? 'mt-3' : '')}>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={onOpenResume}
                  data-testid="profile-summary-view-resume-btn"
                >
                  <Eye className="h-3.5 w-3.5" /> View resume
                </Button>
                {companyResume.approved === false && (
                  <span className="text-[11px] text-amber-800" data-testid="profile-summary-draft-hint">
                    Draft — Approve on Resume tab before sharing with clients
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Module>
  );
}

export function VideoBotSection({ curated, onOpenVideo }) {
  if (curated.length === 0) return null;

  return (
    <Module id="videobot" eyebrow="Self-recorded narrative" title="Video Bot Screening" icon={VideoIcon} inlineTitle>
      <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4" data-testid="demo-videobot-grid">
        {curated.map((r, i) => (
          <VideoPreviewCard
            key={r.question_id}
            mediaKey={r.question_id}
            videoUrl={r.video_url}
            title={r.question_text}
            durationSec={r.duration_seconds}
            starScore={resolveScore(r.human_overall_score, r.overall_score)}
            badge={`Q${i + 1}`}
            onOpen={() => onOpenVideo(r, i + 1)}
            testId={`demo-video-tile-${i + 1}`}
          />
        ))}
      </div>
    </Module>
  );
}

export function McqSection({ mcqRaw, mcqResult, mcqItems, onOpenModal }) {
  if (!mcqResult) return null;

  const categories = (mcqRaw.categories && mcqRaw.categories.length)
    ? mcqRaw.categories
    : deriveMcqCategoriesFromItems(mcqItems);

  return (
    <Module id="mcq" eyebrow="Self-paced assessment" title="IQ Assessment" icon={ClipboardList} inlineTitle>
      <div className="grid gap-5 sm:grid-cols-[150px_1fr]" data-testid="magazine-mcq-section">
        <div className="flex flex-col items-center justify-center rounded-xl border bg-secondary/40 p-4 text-center">
          <p className="text-[34px] font-extrabold leading-none tabular-nums">{mcqResult.score_pct}%</p>
          <p className="mt-1 text-[13px] text-muted-foreground">{mcqResult.correct}/{mcqResult.total_questions} correct</p>
          {categories.length > 0 && (
            <p className="mt-2.5 text-[11px] leading-snug text-muted-foreground">
              categorised across {categories.length} competency area{categories.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {categories.length > 0 ? (
          <ul className="space-y-4" data-testid="magazine-mcq-categories">
            {categories.map((cat) => {
              const pct = cat.total > 0 ? Math.round((cat.correct / cat.total) * 100) : (cat.pct ?? 0);
              return (
                <li key={cat.name} className="comp-row">
                  <div className="mb-1.5 flex justify-between text-[13.5px] font-semibold">
                    <span>{cat.name}</span>
                    <span className="tabular-nums text-muted-foreground">{cat.correct}/{cat.total} · {pct}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  {cat.sample_topics?.length > 0 && (
                    <p className="mt-1 text-[12px] text-muted-foreground">{cat.sample_topics.join(', ')}</p>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {mcqItems.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="max-w-[60ch] text-[13px] text-foreground/80">
            Detailed view of every question, the option the candidate picked, and the correct answer.
          </p>
          <Button variant="outline" size="sm" onClick={onOpenModal} className="shrink-0 gap-1.5" data-testid="magazine-mcq-open-all-btn">
            <ClipboardList className="h-3.5 w-3.5" /> View all questions
          </Button>
        </div>
      )}
    </Module>
  );
}

export function TechAssessmentSection({ techRaw, techResult, techItems, onOpenModal }) {
  if (!techResult) return null;

  const categories = (techRaw.categories && techRaw.categories.length)
    ? techRaw.categories
    : deriveMcqCategoriesFromItems(techItems);

  return (
    <Module id="tech-assessment" eyebrow="Technical assessment" title="Work Assessment" icon={ClipboardList} inlineTitle>
      <div className="grid gap-5 sm:grid-cols-[150px_1fr]" data-testid="magazine-tech-assessment-section">
        <div className="flex flex-col items-center justify-center rounded-xl border bg-secondary/40 p-4 text-center">
          <p className="text-[34px] font-extrabold leading-none tabular-nums">{techResult.score_pct}%</p>
          <p className="mt-1 text-[13px] text-muted-foreground">{techResult.correct}/{techResult.total_questions} correct</p>
          {categories.length > 0 && (
            <p className="mt-2.5 text-[11px] leading-snug text-muted-foreground">
              categorised across {categories.length} competency area{categories.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {categories.length > 0 ? (
          <ul className="space-y-4" data-testid="magazine-tech-categories">
            {categories.map((cat) => {
              const pct = cat.total > 0 ? Math.round((cat.correct / cat.total) * 100) : 0;
              return (
                <li key={cat.name}>
                  <div className="mb-1.5 flex justify-between text-[13.5px] font-semibold">
                    <span>{cat.name}</span>
                    <span className="tabular-nums text-muted-foreground">{cat.correct}/{cat.total} · {pct}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {techItems.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="max-w-[60ch] text-[13px] text-foreground/80">
            Detailed view of every question, the option the candidate picked, and the correct answer.
          </p>
          <Button variant="outline" size="sm" onClick={onOpenModal} className="shrink-0 gap-1.5" data-testid="magazine-tech-open-all-btn">
            <ClipboardList className="h-3.5 w-3.5" /> View all questions
          </Button>
        </div>
      )}
    </Module>
  );
}

export function PanelEvaluationSections({ scoredPanels, shareToken, isDemo, onOpenPanel }) {
  return scoredPanels.map((p, i) => {
    const feedbackPending = !!p.feedback_pending;
    const factors = feedbackPending ? [] : panelFactorScores(p);
    const overall = feedbackPending
      ? null
      : (p.human_overall_score != null ? Number(p.human_overall_score) : null);
    const hasRecording = !!(p.recording_artifact_id || p.recording_url);
    const hasTranscript = !!(p.transcript_text || '').trim();
    const scoresWithheld = !feedbackPending
      && overall == null
      && !factors.some((f) => f.score != null);
    const mediaHint = p.ai_summary || p.ai_report?.summary_narrative?.overall_assessment
      ? 'transcript & AI summary'
      : hasTranscript
        ? 'transcript'
        : hasRecording
          ? 'recording'
          : 'details';
    return (
      <Module
        key={p.id}
        id={`panel-${p.id}`}
        eyebrow={`Panel · ${i + 1} of ${scoredPanels.length}`}
        title="Panel Evaluation"
        icon={Users}
        inlineTitle
      >
        <div className="grid gap-6 lg:grid-cols-[380px_1fr]" data-testid={`panel-eval-grid-${p.id}`}>
          <div>
            {hasRecording ? (
              <PanelRecordingPreview
                panel={p}
                shareToken={shareToken}
                isDemo={isDemo}
                onOpen={() => onOpenPanel(p)}
                testId={`panel-recording-preview-${p.id}`}
              />
            ) : (
              <button
                type="button"
                onClick={() => onOpenPanel(p)}
                className="flex aspect-[4/3] w-full items-center justify-center rounded-xl border bg-secondary px-3 text-center text-[13px] text-muted-foreground hover:bg-secondary/80"
                data-testid={`panel-media-placeholder-${p.id}`}
              >
                <Play className="mr-2 h-4 w-4" /> Panel recording · click for {mediaHint}
              </button>
            )}
          </div>
          <div>
            {feedbackPending ? (
              <p
                className="mb-3 rounded-xl border bg-secondary/40 px-4 py-3 text-[13px] text-muted-foreground"
                data-testid={`panel-feedback-pending-${p.id}`}
              >
                Panellist feedback is in progress. Scores will appear here once a
                panellist has submitted ratings.
              </p>
            ) : null}
            {scoresWithheld ? (
              <p
                className="mb-3 rounded-xl border bg-secondary/40 px-4 py-3 text-[13px] text-muted-foreground"
                data-testid={`panel-scores-withheld-${p.id}`}
              >
                Panel scores are not available for this interview yet. Complete the
                panel AI evaluation or save human panellist ratings to show ratings here.
              </p>
            ) : null}
            {!feedbackPending && !scoresWithheld ? (
              <>
                <div className="mb-3.5 flex items-center justify-between gap-4 rounded-xl border bg-secondary/40 px-4 py-3.5">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Overall Rating</p>
                    <div className="mt-1">
                      <StarRating score={overall} className="scale-110 origin-left" testId={`panel-overall-stars-${p.id}`} />
                    </div>
                  </div>
                  {overall != null && (
                    <p className="text-[26px] font-extrabold tabular-nums leading-none">
                      {Number(overall).toFixed(1)}<span className="text-[14px] font-semibold text-muted-foreground">/5</span>
                    </p>
                  )}
                </div>
                {factors.length > 0 ? (
                  <ul className="divide-y" data-testid={`panel-factor-list-${p.id}`}>
                    {factors.map((f) => (
                      <li key={f.name} className="flex items-center justify-between gap-3 py-2.5">
                        <span className="text-[13.5px] font-semibold">{f.name}</span>
                        <span className="flex items-center gap-2">
                          <StarRating score={f.score} testId={`panel-factor-${p.id}-${f.name}`} />
                          {f.score != null && (
                            <span className="w-8 text-right text-[12.5px] font-bold tabular-nums text-muted-foreground">
                              {Number(f.score).toFixed(1)}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </Module>
    );
  });
}

export function HrEvaluationSection({ hrEval, hasHr }) {
  if (!hasHr) return null;

  return (
    <Module id="hr-evaluation" eyebrow="Behavioral · HR Round" title="HR Evaluation" icon={Users} inlineTitle>
      {hrEval.feedback_pending && !(hrEval.factors || []).length && hrEval.overall_score == null && hrEval.human_overall_score == null ? (
        <p className="text-[13.5px] text-muted-foreground" data-testid="hr-evaluation-pending">
          HR panellist feedback is in progress. Scores will appear here once a
          panellist has submitted ratings.
        </p>
      ) : (
        <HrScoreRings
          overall={resolveScore(hrEval.human_overall_score, hrEval.overall_score)}
          factors={(hrEval.factors || []).map((f) => ({
            name: f.name,
            score: resolveScore(f.human_score, f.score),
          }))}
        />
      )}
    </Module>
  );
}

export function EvaluationSummarySection({ showSummary, weighted }) {
  if (!showSummary) return null;

  return (
    <Module id="final-report" eyebrow="Consolidated" title="Evaluation Summary" icon={Sparkles} inlineTitle>
      <p className="mb-4 text-[12.5px] italic leading-relaxed text-muted-foreground">
        Raw results from every stage, rolled up with a documented weight per stage. No qualitative interpretation applied below — narrative judgment is reserved for the Recommendation section.
      </p>
      {weighted.rows.length === 0 ? (
        <div
          className="rounded-xl border bg-secondary/40 p-6 text-sm text-muted-foreground"
          data-testid="evaluation-summary-empty"
        >
          No evaluation data available
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]" data-testid="evaluation-summary-table">
              <thead>
                <tr className="text-left text-[10.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  <th className="border-b px-2.5 pb-2">Stage</th>
                  <th className="border-b px-2.5 pb-2">Raw Result</th>
                  <th className="border-b px-2.5 pb-2">Score</th>
                  <th className="border-b px-2.5 pb-2">Weight</th>
                </tr>
              </thead>
              <tbody>
                {weighted.rows.map((r) => (
                  <tr key={r.key} className="border-b last:border-0">
                    <td className="px-2.5 py-2.5 font-semibold text-foreground">{r.stage}</td>
                    <td className="px-2.5 py-2.5 text-foreground/80">{r.raw}</td>
                    <td className="px-2.5 py-2.5 text-foreground/80">{r.scoreLabel}</td>
                    <td className="px-2.5 py-2.5 text-muted-foreground">{r.weightPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {weighted.compositePct != null && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-5 border-t pt-4">
              <div>
                <p className="text-[36px] font-extrabold leading-none tabular-nums" data-testid="evaluation-summary-composite">
                  {weighted.compositePct}%
                </p>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  Weighted composite across {weighted.rows.length} scored stage{weighted.rows.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="max-w-[52ch] rounded-xl border bg-secondary/40 px-3 py-2.5 font-mono text-[11.5px] leading-relaxed text-muted-foreground" data-testid="evaluation-summary-formula">
                {weighted.formula}
              </div>
            </div>
          )}
        </>
      )}
    </Module>
  );
}
